import React from 'react';
import style from './style.module.scss';
import { observer } from 'mobx-react';
import {
    Card,
    DatePicker,
    Statistic,
    List,
    Avatar,
    Spin,
    Divider,
    Table,
    Form,
} from 'antd';

import { useAuthStore, useNhlGamesStore } from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { useNavigation } from '../../utils/use-navigation';
import { KpiSearchParams } from '../../stores/nhl-game';
import moment from 'moment';
import Meta from 'antd/lib/card/Meta';
import { ExportButton } from '../../components/action-buttons';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { downloadCSVFile } from '../../utils/csv';
import { Text } from '../../utils/text';
import {
    handleDatePickerChange,
    isValidDateRange,
} from '../../utils/date-picker-helpers';
import { ColumnProps } from 'antd/lib/table';
import { NhlGameStoresPinsKpi } from '../../types/nhl-game';
import { oktaAuth } from '~utils/okta';

const { RangePicker } = DatePicker;

export const NhlGamesDashboardPage = observer(() => {
    const store = useNhlGamesStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const search = useSearchParams<KpiSearchParams>();

    React.useEffect(() => {
        // Only make API calls if we have valid start and end dates
        if (isValidDateRange(search.params)) {
            store.getKpiNhlGame(search.params);
            store.getNhlGameStoresPinsKpi(search.params);
        }
    }, [store, search.params]);

    const downloadCsv = async () => {
        const { startDate, endDate } = search.params;

        if (!startDate || !endDate) {
            NotificationManager.showError(
                Text.notificationManager.nhlGame.errorDateExportDashboard
            );
        } else {
            try {
                const newParticipant = await store.getKpiNhlGameForExport({
                    startDate,
                    endDate,
                });
                const csvData = newParticipant!.map((data) => ({
                    'User ID': data.userId,
                    'First Name': data.firstName,
                    'Last Name': data.lastName,
                    Email: data.email,
                    'Phone Number': data.phoneNumber,
                    Language: data.language,
                    'Created Date': data.createdAt,
                }));
                return downloadCSVFile(
                    csvData,
                    Text.nhlGame.dashboardContainer.csvTitle
                );
            } catch (error) {
                NotificationManager.showError(
                    Text.notificationManager.general.errorExportFile
                );
            }
        }
    };

    const downloadStoreCsv = async () => {
        const token = oktaAuth.getAccessToken() as string;
        const url = new URL(
            `${
                import.meta.env.REACT_APP_API_URL
            }/api-cms/nhl-games/stores/kpi/download`
        );
        const searchCriterias: Record<string, string> = {};
        if (search.params.startDate)
            searchCriterias.startDate = search.params.startDate;
        if (search.params.endDate)
            searchCriterias.endDate = search.params.endDate;
        url.search = new URLSearchParams(searchCriterias).toString();
        const response = await fetch(url.toString(), {
            headers: { jwt: token },
        });
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        document.body.appendChild(anchor);
        anchor.href = blobUrl;
        anchor.download = 'stores_pins.csv';
        anchor.click();
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(anchor);
    };

    const columns = React.useMemo<ColumnProps<NhlGameStoresPinsKpi>[]>(
        () => [
            {
                title: Text.nhlGame.dashboardContainer.stores.table.nref,
                dataIndex: 'nref',
                key: 'nref',
            },
            {
                title: Text.nhlGame.dashboardContainer.stores.table.adress,
                dataIndex: 'address',
                render: (_: unknown, record: NhlGameStoresPinsKpi) =>
                    `${record.street}, ${record.city}, ${record.postalCode}`,
            },
            {
                title:
                    Text.nhlGame.dashboardContainer.stores.table.pinsValidated,
                dataIndex: 'validatedTbPinCount',
                key: 'validatedTbPinCount',
            },
        ],
        []
    );

    return (
        <>
            <PageTopBar title={Text.nhlGame.dashboardContainer.pageTitle} />

            <div className="content_with_margins">
                <div className={style.date_picker_row}>
                    <Form>
                        <Form.Item label="Period" colon={false}>
                            <RangePicker
                                allowEmpty={[true, true]}
                                placeholder={[
                                    Text.nhlGame.dashboardContainer.rangerPicker
                                        .placeholder.start,
                                    Text.nhlGame.dashboardContainer.rangerPicker
                                        .placeholder.end,
                                ]}
                                defaultValue={[
                                    search.params.startDate
                                        ? moment(search.params.startDate)
                                        : null,
                                    search.params.endDate
                                        ? moment(search.params.endDate)
                                        : null,
                                ]}
                                onChange={(dates) => {
                                    handleDatePickerChange(
                                        dates,
                                        (validDates) => {
                                            search.setParams({
                                                startDate: validDates[0].format(),
                                                endDate: validDates[1].format(),
                                            });
                                        },
                                        () => {
                                            search.setParams({
                                                startDate: undefined,
                                                endDate: undefined,
                                            });
                                        }
                                    );
                                }}
                                format="YYYY-MM-DD"
                            />
                        </Form.Item>
                    </Form>
                    <ExportButton onClick={downloadCsv}>
                        {Text.nhlGame.dashboardContainer.downloadCsv}
                    </ExportButton>
                </div>
                <div className={style.input_row}>
                    <h1>
                        {Text.nhlGame.dashboardContainer.sectionTitle.users}
                    </h1>
                </div>
                <div className={style.input_row}>
                    {store.isFetchingKpi ? (
                        <Spin />
                    ) : (
                        <div className={style.input_row}>
                            <div className={`${style.fluid_input_col}`}>
                                <Card className={style.stat_card}>
                                    <Statistic
                                        className={style.stats}
                                        title={
                                            Text.nhlGame.dashboardContainer
                                                .stats.newUser
                                        }
                                        value={store.kpi?.newClients}
                                    ></Statistic>
                                </Card>
                            </div>
                            <div className={`${style.fluid_input_col}`}>
                                <Card className={style.stat_card}>
                                    <Statistic
                                        className={style.stats}
                                        title={
                                            Text.nhlGame.dashboardContainer
                                                .stats.newPlayers
                                        }
                                        value={store.kpi?.newParticipations}
                                    ></Statistic>
                                </Card>
                            </div>
                            <div className={`${style.fluid_input_col}`}>
                                <Card className={style.stat_card}>
                                    <Statistic
                                        className={style.stats}
                                        title={
                                            Text.nhlGame.dashboardContainer
                                                .stats.visits
                                        }
                                        value={
                                            store.kpi?.firebaseMetrics
                                                .eventCount
                                        }
                                    ></Statistic>

                                    <Divider type="horizontal" />

                                    <Meta
                                        description={`${Text.nhlGame.dashboardContainer.stats.distinctUsers}
                                                ${store.kpi?.firebaseMetrics.totalUsers}`}
                                    />
                                </Card>
                            </div>
                        </div>
                    )}
                </div>

                <div className={style.section}>
                    <div className={style.input_row}>
                        <h1>
                            {
                                Text.nhlGame.dashboardContainer.sectionTitle
                                    .coupons
                            }
                        </h1>
                    </div>
                    <div className={style.input_row}>
                        {store.isFetchingKpi ? (
                            <Spin />
                        ) : (
                            <div className={style.input_row}>
                                <div className={`${style.fluid_input_col}`}>
                                    <Card className={style.stat_card}>
                                        <Statistic
                                            className={style.stats}
                                            title={
                                                Text.nhlGame.dashboardContainer
                                                    .stats.couponsWon
                                            }
                                            value={
                                                store.kpi?.kpiPromotions.grants
                                            }
                                        ></Statistic>
                                    </Card>
                                </div>
                                <div className={`${style.fluid_input_col}`}>
                                    <Card className={style.stat_card}>
                                        <Statistic
                                            className={style.stats}
                                            title={
                                                Text.nhlGame.dashboardContainer
                                                    .stats.couponsRedeemed
                                            }
                                            value={
                                                store.kpi?.kpiPromotions
                                                    .activations
                                            }
                                        ></Statistic>
                                    </Card>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={style.section}>
                    <div className={style.input_row}>
                        <h1>
                            {Text.nhlGame.dashboardContainer.sectionTitle.teams}
                        </h1>
                    </div>
                    <div className={style.input_row}>
                        {store.isFetchingKpi ? (
                            <Spin />
                        ) : (
                            <>
                                <Card
                                    title={
                                        <span>
                                            {store.kpi?.teamsSelected}/32 are
                                            selected
                                        </span>
                                    }
                                >
                                    <List
                                        itemLayout="horizontal"
                                        className={style.list}
                                        dataSource={
                                            store.kpi?.mostSelectedTeams
                                        }
                                        renderItem={(item) => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar
                                                            src={item.logo}
                                                        />
                                                    }
                                                    title={
                                                        <h2>
                                                            {item.city}{' '}
                                                            {item.name}
                                                        </h2>
                                                    }
                                                    description={`${Text.nhlGame.dashboardContainer.teamSelected} ${item.count}`}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            </>
                        )}
                    </div>
                </div>
                <div className={style.section}>
                    <h1>
                        {Text.nhlGame.dashboardContainer.sectionTitle.stores}
                    </h1>
                    <ExportButton onClick={downloadStoreCsv}>
                        {Text.nhlGame.dashboardContainer.downloadCsv}
                    </ExportButton>
                </div>
            </div>

            <Table columns={columns} dataSource={store.storesPinsKpi || []} />
        </>
    );
});
